import React, { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  Calendar as CalendarIcon,
  Clock,
  User,
  Phone,
  MapPin,
  ClipboardList,
  Wrench,
  Home,
  Search,
  Loader2,
  Edit,
  Trash2,
  X,
  FileText
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import CreateSurveyDialog from './SurveyManagement/CreateSurveyDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppointmentCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDayModal, setShowDayModal] = useState(false);
  const [selectedDayAppointments, setSelectedDayAppointments] = useState([]);
  const [selectedDayLabel, setSelectedDayLabel] = useState('');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Survey dialog state
  const [showSurveyDialog, setShowSurveyDialog] = useState(false);
  const [surveyCustomerData, setSurveyCustomerData] = useState(null);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    appointment_type: 'survey',
    date: '',
    start_time: '09:00',
    end_time: '10:00',
    notes: '',
    assigned_to: '',
    isNewCustomer: false
  });

  // Calendar helpers
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Load appointments for current month
  useEffect(() => {
    loadAppointments();
  }, [currentDate]);

  const loadAppointments = async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const response = await axios.get(`${API}/appointments/month/${year}/${month}`);
      setAppointments(response.data.data || []);
    } catch (error) {
      console.error('Error loading appointments:', error);
      toast({
        title: "Error",
        description: "Failed to load appointments",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Search customers
  const searchCustomers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/customers?search=${encodeURIComponent(query)}&limit=10`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setSearchResults(response.data.customers || []);
    } catch (error) {
      console.error('Error searching customers:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchCustomers(customerSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  // Get appointments for a specific day
  const getAppointmentsForDay = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return appointments.filter(apt => apt.date === dateStr);
  };

  // Navigate months
  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Handle day click
  const handleDayClick = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
    setFormData(prev => ({ ...prev, date: dateStr }));
    setShowAddModal(true);
  };

  // Handle appointment click
  const handleAppointmentClick = (appointment, e) => {
    e.stopPropagation();
    setSelectedAppointment(appointment);
    setShowViewModal(true);
  };

  // Handle "view more" click on a day
  const handleViewMoreClick = (day, dayAppointments, e) => {
    e.stopPropagation();
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dateObj = new Date(dateStr + 'T00:00:00');
    setSelectedDayLabel(dateObj.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }));
    setSelectedDayAppointments(dayAppointments);
    setSelectedDate(dateStr);
    setShowDayModal(true);
  };

  // Select customer from search results
  const selectCustomer = (customer) => {
    setFormData(prev => ({
      ...prev,
      customer_id: customer.id,
      customer_name: customer.name || customer.full_name || '',
      customer_email: customer.email || '',
      customer_phone: customer.phone || '',
      customer_address: customer.address || customer.address1 || customer.postcode || customer.postal_code || ''
    }));
    setCustomerSearch('');
    setSearchResults([]);
  };

  // Create appointment
  const handleCreateAppointment = async () => {
    if (!formData.customer_name || !formData.date || !formData.start_time) {
      toast({
        title: "Missing Information",
        description: "Please fill in customer name, date, and start time",
        variant: "destructive"
      });
      return;
    }

    try {
      // Don't send isNewCustomer to backend
      const { isNewCustomer, ...appointmentData } = formData;
      const response = await axios.post(`${API}/appointments/`, appointmentData);
      
      const typeLabel = getAppointmentTypeLabel(formData.appointment_type);
      let message = `${typeLabel} scheduled for ${formData.customer_name}`;
      
      if (formData.appointment_type === 'survey') {
        message += '. Job created in pipeline.';
      }
      if (isNewCustomer) {
        message += ' Customer added to database.';
      }
      
      toast({
        title: "Appointment Created",
        description: message
      });
      
      setShowAddModal(false);
      resetForm();
      loadAppointments();
    } catch (error) {
      console.error('Error creating appointment:', error);
      toast({
        title: "Error",
        description: "Failed to create appointment",
        variant: "destructive"
      });
    }
  };

  // Update appointment status
  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    try {
      await axios.patch(`${API}/appointments/${appointmentId}`, { status: newStatus });
      
      toast({
        title: "Status Updated",
        description: `Appointment marked as ${newStatus}`
      });
      
      loadAppointments();
      setShowViewModal(false);
    } catch (error) {
      console.error('Error updating appointment:', error);
      toast({
        title: "Error",
        description: "Failed to update appointment",
        variant: "destructive"
      });
    }
  };

  // Delete appointment
  const deleteAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) return;
    
    try {
      await axios.delete(`${API}/appointments/${appointmentId}`);
      
      toast({
        title: "Appointment Deleted",
        description: "The appointment has been removed"
      });
      
      loadAppointments();
      setShowViewModal(false);
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast({
        title: "Error",
        description: "Failed to delete appointment",
        variant: "destructive"
      });
    }
  };

  // Start survey from appointment
  const startSurveyFromAppointment = (appointment) => {
    // Pre-fill customer data for the survey
    setSurveyCustomerData({
      id: appointment.customer_id,
      name: appointment.customer_name,
      email: appointment.customer_email,
      phone: appointment.customer_phone,
      address: appointment.customer_address
    });
    setShowViewModal(false);
    setShowSurveyDialog(true);
  };

  // Create survey function for the dialog
  const createSurvey = async (surveyData) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/surveys/create`, surveyData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error creating survey:', error);
      throw error;
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      customer_id: '',
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      customer_address: '',
      appointment_type: 'survey',
      date: '',
      start_time: '09:00',
      end_time: '10:00',
      notes: '',
      assigned_to: '',
      isNewCustomer: false
    });
    setCustomerSearch('');
    setSearchResults([]);
  };

  // Get appointment type label and color
  const getAppointmentTypeLabel = (type) => {
    switch(type) {
      case 'survey': return 'Survey';
      case 'installation': return 'Installation';
      case 'service_call': return 'Service Call';
      default: return type;
    }
  };

  const getAppointmentTypeColor = (type) => {
    switch(type) {
      case 'survey': return 'bg-blue-500';
      case 'installation': return 'bg-green-500';
      case 'service_call': return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  };

  const getAppointmentTypeIcon = (type) => {
    switch(type) {
      case 'survey': return <ClipboardList className="h-4 w-4" />;
      case 'installation': return <Home className="h-4 w-4" />;
      case 'service_call': return <Wrench className="h-4 w-4" />;
      default: return <CalendarIcon className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      scheduled: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-green-100 text-green-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800',
      rescheduled: 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Calculate calendar days
  const calendarDays = useMemo(() => {
    const days = [];
    const prevMonthDays = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0).getDate();
    
    // Previous month days
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      days.push({ day: prevMonthDays - i, isCurrentMonth: false });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, isCurrentMonth: true });
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ day: i, isCurrentMonth: false });
    }
    
    return days;
  }, [currentDate, daysInMonth, firstDayOfMonth]);

  // Check if day is today
  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && 
           currentDate.getMonth() === today.getMonth() && 
           currentDate.getFullYear() === today.getFullYear();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Appointment Calendar</h2>
          <p className="text-gray-600">Manage surveys, installations, and service calls</p>
        </div>
        <Button onClick={() => { resetForm(); setShowAddModal(true); }} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          New Appointment
        </Button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
          <span className="text-sm text-gray-600">Survey</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="text-sm text-gray-600">Installation</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-orange-500"></div>
          <span className="text-sm text-gray-600">Service Call</span>
        </div>
      </div>

      {/* Calendar */}
      <Card className="shadow-lg">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="icon" onClick={prevMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h3 className="text-xl font-semibold min-w-[200px] text-center">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h3>
              <Button variant="outline" size="icon" onClick={nextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <Button variant="outline" onClick={goToToday}>
              Today
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {/* Day headers */}
              {dayNames.map(day => (
                <div key={day} className="text-center font-semibold text-gray-500 py-2 text-sm">
                  {day}
                </div>
              ))}
              
              {/* Calendar days */}
              {calendarDays.map((dayInfo, index) => {
                const dayAppointments = dayInfo.isCurrentMonth ? getAppointmentsForDay(dayInfo.day) : [];
                const isTodayDate = dayInfo.isCurrentMonth && isToday(dayInfo.day);
                
                return (
                  <div
                    key={index}
                    onClick={() => dayInfo.isCurrentMonth && handleDayClick(dayInfo.day)}
                    className={`
                      min-h-[100px] p-1 border rounded-lg cursor-pointer transition-colors
                      ${dayInfo.isCurrentMonth ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 text-gray-400 cursor-default'}
                      ${isTodayDate ? 'ring-2 ring-blue-500 ring-offset-1' : ''}
                    `}
                  >
                    <div className={`text-sm font-medium mb-1 ${isTodayDate ? 'text-blue-600' : ''}`}>
                      {dayInfo.day}
                    </div>
                    <div className="space-y-1">
                      {dayAppointments.slice(0, 3).map((apt, aptIndex) => (
                        <div
                          key={aptIndex}
                          onClick={(e) => handleAppointmentClick(apt, e)}
                          className={`
                            text-xs text-white px-1 py-0.5 rounded truncate cursor-pointer
                            ${getAppointmentTypeColor(apt.appointment_type)}
                            hover:opacity-80 transition-opacity
                          `}
                          title={`${apt.start_time} - ${apt.customer_name}`}
                        >
                          {apt.start_time} {apt.customer_name.split(' ')[0]}
                        </div>
                      ))}
                      {dayAppointments.length > 3 && (
                        <div 
                          onClick={(e) => handleViewMoreClick(dayInfo.day, dayAppointments, e)}
                          className="text-xs text-blue-600 pl-1 cursor-pointer hover:text-blue-800 hover:underline font-medium"
                          data-testid={`view-more-${dayInfo.day}`}
                        >
                          +{dayAppointments.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Appointment Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">New Appointment</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Customer Selection Mode */}
            <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
              <button
                onClick={() => {
                  setFormData(prev => ({ ...prev, customer_id: '', customer_name: '', customer_email: '', customer_phone: '', customer_address: '', isNewCustomer: false }));
                  setCustomerSearch('');
                }}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  !formData.isNewCustomer 
                    ? 'bg-white shadow text-[#2F4F4F]' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Search className="h-4 w-4 inline mr-2" />
                Existing Customer
              </button>
              <button
                onClick={() => {
                  setFormData(prev => ({ ...prev, customer_id: '', customer_name: '', customer_email: '', customer_phone: '', customer_address: '', isNewCustomer: true }));
                  setCustomerSearch('');
                  setSearchResults([]);
                }}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  formData.isNewCustomer 
                    ? 'bg-white shadow text-[#2F4F4F]' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Plus className="h-4 w-4 inline mr-2" />
                New Customer
              </button>
            </div>

            {/* Existing Customer Search */}
            {!formData.isNewCustomer && !formData.customer_name && (
              <div>
                <Label>Search Customer</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    placeholder="Search by name, email, or phone..."
                    className="pl-10"
                  />
                  {isSearching && (
                    <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
                  )}
                </div>
                
                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="mt-2 border rounded-lg shadow-lg max-h-48 overflow-y-auto bg-white">
                    {searchResults.map((customer) => (
                      <div
                        key={customer.id}
                        onClick={() => selectCustomer(customer)}
                        className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                      >
                        <div className="font-medium">{customer.name || customer.full_name}</div>
                        <div className="text-sm text-gray-500">
                          {customer.email} {customer.phone && `• ${customer.phone}`}
                        </div>
                        {(customer.address || customer.address1) && (
                          <div className="text-xs text-gray-400 mt-1">{customer.address || customer.address1}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {customerSearch.length >= 2 && searchResults.length === 0 && !isSearching && (
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">No customers found. 
                      <button 
                        onClick={() => setFormData(prev => ({ ...prev, isNewCustomer: true, customer_name: customerSearch }))}
                        className="ml-1 underline font-medium"
                      >
                        Create new customer?
                      </button>
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Selected Customer Display */}
            {!formData.isNewCustomer && formData.customer_name && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-green-800">{formData.customer_name}</div>
                    <div className="text-sm text-green-600">
                      {formData.customer_email} {formData.customer_phone && `• ${formData.customer_phone}`}
                    </div>
                    {formData.customer_address && (
                      <div className="text-xs text-green-600 mt-1">{formData.customer_address}</div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFormData(prev => ({ ...prev, customer_id: '', customer_name: '', customer_email: '', customer_phone: '', customer_address: '' }))}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* New Customer Form */}
            {formData.isNewCustomer && (
              <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <User className="h-4 w-4 text-blue-600" />
                  <span className="font-medium text-blue-800">New Customer Details</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label htmlFor="new_customer_name">Name *</Label>
                    <Input
                      id="new_customer_name"
                      value={formData.customer_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                      placeholder="Full name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new_customer_email">Email</Label>
                    <Input
                      id="new_customer_email"
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                      placeholder="email@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new_customer_phone">Phone</Label>
                    <Input
                      id="new_customer_phone"
                      value={formData.customer_phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                      placeholder="07xxx xxxxxx"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="new_customer_address">Address</Label>
                    <Input
                      id="new_customer_address"
                      value={formData.customer_address}
                      onChange={(e) => setFormData(prev => ({ ...prev, customer_address: e.target.value }))}
                      placeholder="Full address"
                    />
                  </div>
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  ✓ Customer will be automatically added to your database
                </p>
              </div>
            )}

            {/* Appointment Type */}
            <div>
              <Label>Appointment Type *</Label>
              <Select
                value={formData.appointment_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, appointment_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="survey">
                    <div className="flex items-center gap-2">
                      <ClipboardList className="h-4 w-4 text-blue-500" />
                      Survey
                    </div>
                  </SelectItem>
                  <SelectItem value="installation">
                    <div className="flex items-center gap-2">
                      <Home className="h-4 w-4 text-green-500" />
                      Installation
                    </div>
                  </SelectItem>
                  <SelectItem value="service_call">
                    <div className="flex items-center gap-2">
                      <Wrench className="h-4 w-4 text-orange-500" />
                      Service Call
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="start_time">Start Time *</Label>
                <Input
                  id="start_time"
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="end_time">End Time</Label>
                <Input
                  id="end_time"
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                />
              </div>
            </div>

            {/* Customer Contact (if not from database) */}
            {!formData.customer_id && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="customer_phone">Phone</Label>
                  <Input
                    id="customer_phone"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                    placeholder="Phone number"
                  />
                </div>
                <div>
                  <Label htmlFor="customer_email">Email</Label>
                  <Input
                    id="customer_email"
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                    placeholder="Email address"
                  />
                </div>
              </div>
            )}

            {/* Address */}
            <div>
              <Label htmlFor="customer_address">Address</Label>
              <Input
                id="customer_address"
                value={formData.customer_address}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_address: e.target.value }))}
                placeholder="Full address or postcode"
              />
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional information..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateAppointment}>
              Create Appointment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Appointment Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedAppointment && getAppointmentTypeIcon(selectedAppointment.appointment_type)}
              {selectedAppointment && getAppointmentTypeLabel(selectedAppointment.appointment_type)}
            </DialogTitle>
          </DialogHeader>
          
          {selectedAppointment && (
            <div className="space-y-4 py-4">
              {/* Status */}
              <div className="flex items-center justify-between">
                <Badge className={getStatusBadge(selectedAppointment.status)}>
                  {selectedAppointment.status.replace('_', ' ').toUpperCase()}
                </Badge>
                <div className="text-sm text-gray-500">
                  {new Date(selectedAppointment.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
              </div>

              {/* Customer Info */}
              <Card>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">{selectedAppointment.customer_name}</span>
                  </div>
                  {selectedAppointment.customer_phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <span>{selectedAppointment.customer_phone}</span>
                    </div>
                  )}
                  {selectedAppointment.customer_address && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span>{selectedAppointment.customer_address}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Time */}
              <div className="flex items-center gap-2 text-gray-600">
                <Clock className="h-4 w-4" />
                <span>{selectedAppointment.start_time}</span>
                {selectedAppointment.end_time && (
                  <>
                    <span>-</span>
                    <span>{selectedAppointment.end_time}</span>
                  </>
                )}
              </div>

              {/* Notes */}
              {selectedAppointment.notes && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Label className="text-xs text-gray-500">Notes</Label>
                  <p className="text-sm">{selectedAppointment.notes}</p>
                </div>
              )}

              {/* Status Actions */}
              <div className="border-t pt-4">
                <Label className="text-xs text-gray-500 mb-2 block">Update Status</Label>
                <div className="flex flex-wrap gap-2">
                  {selectedAppointment.status !== 'confirmed' && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => updateAppointmentStatus(selectedAppointment.id, 'confirmed')}
                    >
                      Confirm
                    </Button>
                  )}
                  {selectedAppointment.status !== 'completed' && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-green-600 border-green-600 hover:bg-green-50"
                      onClick={() => updateAppointmentStatus(selectedAppointment.id, 'completed')}
                    >
                      Mark Complete
                    </Button>
                  )}
                  {selectedAppointment.status !== 'cancelled' && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-600 border-red-600 hover:bg-red-50"
                      onClick={() => updateAppointmentStatus(selectedAppointment.id, 'cancelled')}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>

              {/* Start Survey Button - only for survey appointments */}
              {selectedAppointment.appointment_type === 'survey' && selectedAppointment.status !== 'cancelled' && (
                <div className="border-t pt-4">
                  <Button 
                    onClick={() => startSurveyFromAppointment(selectedAppointment)}
                    className="w-full bg-[#8CB89F] hover:bg-[#7aa88f] text-white"
                    data-testid="start-survey-btn"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Start Survey for {selectedAppointment.customer_name}
                  </Button>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Opens survey form with customer details pre-filled
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="destructive" 
              size="sm"
              onClick={() => selectedAppointment && deleteAppointment(selectedAppointment.id)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <Button variant="outline" onClick={() => setShowViewModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Day Detail Modal - shows all appointments for a day */}
      <Dialog open={showDayModal} onOpenChange={setShowDayModal}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg">{selectedDayLabel}</DialogTitle>
            <p className="text-sm text-gray-500">{selectedDayAppointments.length} appointment{selectedDayAppointments.length !== 1 ? 's' : ''}</p>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {selectedDayAppointments
              .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))
              .map((apt, idx) => (
              <div
                key={idx}
                onClick={() => { setSelectedAppointment(apt); setShowDayModal(false); setShowViewModal(true); }}
                className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 cursor-pointer transition-colors"
                data-testid={`day-apt-${idx}`}
              >
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getAppointmentTypeColor(apt.appointment_type)}`} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{apt.customer_name}</p>
                  <p className="text-xs text-gray-500">{apt.appointment_type?.replace('_', ' ')}</p>
                </div>
                <div className="text-right text-sm">
                  <p className="font-medium">{apt.start_time}</p>
                  {apt.end_time && <p className="text-xs text-gray-400">to {apt.end_time}</p>}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              onClick={() => { setShowDayModal(false); setFormData(prev => ({ ...prev, date: selectedDate })); setShowAddModal(true); }}
              className="bg-[#8CB89F] hover:bg-[#7aa88f] w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Appointment on This Day
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Survey Dialog - triggered from appointment */}
      <CreateSurveyDialog
        open={showSurveyDialog}
        onOpenChange={setShowSurveyDialog}
        onCreated={() => {
          setShowSurveyDialog(false);
          setSurveyCustomerData(null);
          toast({
            title: "Survey Created",
            description: "Survey has been created successfully. You can view it in the Surveys tab."
          });
        }}
        createSurvey={createSurvey}
        initialCustomerData={surveyCustomerData}
      />
    </div>
  );
};

export default AppointmentCalendar;
